/**
 * Описание задачи — визуальный редактор с оформлением (shared/ui/richtext).
 *
 * Просмотр: оформленный текст; пользователь с правом правки отмечает пункты
 * чек-листа прямо здесь — каждый пункт сохраняется отдельной операцией,
 * не трогая остальное описание. У архивной задачи и без права — только чтение
 * (копирование кода и переход по ссылкам остаются).
 *
 * Правка: изменения живут в локальном черновике до «Сохранить». «Отмена»
 * возвращает исходное описание; закрыть редактор с изменениями можно только
 * подтвердив потерю. Конфликт версий не перезаписывает чужую правку молча
 * и не теряет черновик — решение за человеком.
 *
 * Вставленный в поле файл (Ctrl+V) уходит в файлы задачи, как и прежде:
 * снимок экрана прикладывают к описанию, чтобы он остался в карточке.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { useAppDispatch } from "@/app/hooks";
import {
  api,
  docKey,
  fromPlain,
  isEmptyDoc,
  MAX_DESCRIPTION_CHARS,
  plainText,
  type RichDoc,
  type WsError,
} from "@taskflow/client-core";
import {
  AttachButton,
  Button,
  ConfirmDialog,
  IconButton,
  PendingFiles,
  RichTextEditor,
  RichTextView,
  toast,
  usePendingFiles,
} from "@/shared/ui";
import { Pencil } from "@/shared/ui/motionIcons";

import {
  type TaskDetail,
  useSetChecklistItemMutation,
  useUpdateTaskMutation,
} from "@taskflow/client-core/tasks";
import { ATTACH_OPTIONS } from "../attachmentRules";
import { useAttachmentFiles } from "../useAttachmentFiles";
import { Section } from "./Section";

/** Описание задачи в виде документа: прежнее текстовое — абзацами. */
function descriptionDocOf(task: TaskDetail): RichDoc | null {
  if (task.descriptionDoc !== undefined && task.descriptionDoc !== null) return task.descriptionDoc;
  return task.description === null ? null : fromPlain(task.description);
}

export function TaskDescription({
  task,
  canEdit,
  canAttach,
  onDirtyChange,
}: {
  task: TaskDetail;
  /** Право правки задачи и задача не в архиве. */
  canEdit: boolean;
  /** Право прикладывать файлы (file.add). */
  canAttach: boolean;
  /** Есть несохранённые правки — панель спросит перед закрытием. */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const dispatch = useAppDispatch();
  const [update, { isLoading }] = useUpdateTaskMutation();
  const [setItem] = useSetChecklistItemMutation();
  const { upload } = useAttachmentFiles(task.key);
  const saved = useMemo(() => descriptionDocOf(task), [task]);

  const [editingState, setEditing] = useState(false);
  // Правка стала недоступна (задачу архивировали) — редактор закрыт.
  const editing = editingState && canEdit;
  // Черновик и то, с чего правка началась: «Отмена» возвращает именно его.
  const [draft, setDraft] = useState<RichDoc | null>(null);
  const [origin, setOrigin] = useState<RichDoc | null>(null);
  const [conflict, setConflict] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [uploading, setUploading] = useState(false);
  const attach = canAttach ? ATTACH_OPTIONS : undefined;
  const tray = usePendingFiles(attach);

  const dirty = editing && (docKey(draft) !== docKey(origin) || tray.files.length > 0);
  const tooLong = draft !== null && plainText(draft).length > MAX_DESCRIPTION_CHARS;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  // Обновление или закрытие вкладки с несохранённым описанием — через
  // стандартное предупреждение браузера.
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const start = () => {
    setDraft(saved);
    setOrigin(saved);
    setConflict(false);
    setEditing(true);
  };

  const close = () => {
    tray.clear();
    setConflict(false);
    setConfirmDiscard(false);
    setEditing(false);
  };

  const cancel = () => {
    if (dirty) setConfirmDiscard(true);
    else close();
  };

  const save = async (version = task.version) => {
    if (isLoading || tooLong) return;
    const doc = draft === null || isEmptyDoc(draft) ? null : draft;
    try {
      await update({ key: task.key, version, descriptionDoc: doc }).unwrap();
    } catch (raw) {
      const error = raw as WsError;
      if (error.status === 409) {
        // Черновик остаётся в редакторе; свежая версия задачи подтягивается,
        // и человек решает — сохранить своё поверх или отказаться.
        setConflict(true);
        dispatch(api.util.invalidateTags(["Task", "Events"]));
      } else {
        toast.error(error, "Описание не сохранено — текст остался в редакторе");
      }
      return;
    }
    toast.success("Описание сохранено");
    // Файлы грузятся после сохранения текста: неудача с файлом не должна
    // отменять уже сохранённое описание, о ней сообщает сама загрузка.
    const files = tray.files;
    close();
    if (files.length > 0) {
      setUploading(true);
      try {
        for (const file of files) {
          if (await upload(file)) toast.success("Файл приложен", file.name);
        }
      } finally {
        setUploading(false);
      }
    }
  };

  const toggleItem = useCallback(
    async (itemId: string, checked: boolean) => {
      try {
        await setItem({ key: task.key, itemId, checked }).unwrap();
        return true;
      } catch (raw) {
        toast.error(raw as WsError, "Отметка не сохранена");
        return false;
      }
    },
    [setItem, task.key],
  );

  return (
    <Section
      title="Описание"
      action={
        canEdit && !editing ? (
          <IconButton icon={Pencil} label="Редактировать описание" size="sm" onClick={start} />
        ) : undefined
      }
    >
      {editing ? (
        <div className="flex min-w-0 flex-col gap-2">
          <RichTextEditor
            value={draft}
            onChange={setDraft}
            ariaLabel="Описание задачи"
            autoFocus
            maxChars={MAX_DESCRIPTION_CHARS}
            onPasteFiles={(files) => tray.add(files)}
          />
          {conflict && (
            <div role="alert" className="rounded-md bg-warning-soft px-3 py-2 text-[13px] text-warning">
              <p className="font-semibold">Пока вы редактировали, задачу изменили.</p>
              <p className="mt-0.5 text-fg">
                Ваш текст остался в редакторе. Сохранить его поверх новой версии или
                отказаться от своих правок?
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" loading={isLoading} onClick={() => void save(task.version)}>
                  Сохранить мою версию
                </Button>
                <Button size="sm" variant="secondary" onClick={close}>
                  Отказаться от правок
                </Button>
              </div>
            </div>
          )}
          {tray.files.length > 0 && (
            <>
              <PendingFiles
                files={tray.files}
                label="Файлы к описанию"
                disabled={isLoading}
                onRemove={tray.remove}
              />
              <p className="text-[11px] text-faint">
                Вставленное окажется в файлах задачи после сохранения
              </p>
            </>
          )}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              loading={isLoading || uploading}
              disabled={tooLong || conflict}
              onClick={() => void save()}
            >
              Сохранить
            </Button>
            <Button size="sm" variant="secondary" disabled={isLoading} onClick={cancel}>
              Отмена
            </Button>
            {attach && <AttachButton attach={attach} disabled={isLoading} onPick={tray.add} />}
          </div>
        </div>
      ) : saved === null || isEmptyDoc(saved) ? (
        <p className="text-[13px] text-faint">Описание не заполнено</p>
      ) : (
        <RichTextView
          doc={saved}
          ariaLabel="Описание задачи"
          {...(canEdit ? { toggleItem } : {})}
        />
      )}

      <ConfirmDialog
        open={confirmDiscard}
        title="Отменить изменения описания?"
        description="Несохранённый текст будет потерян, останется прежнее описание."
        confirmLabel="Отменить изменения"
        tone="danger"
        onConfirm={close}
        onCancel={() => setConfirmDiscard(false)}
      />
    </Section>
  );
}
